<template>
  <div class="mic-input-container">
    <div class="bg-container pd-horizontal-4">
      <LogoMainNav v-if="listButtonVisible.includes('LOGO')" :title="t('mic_input_settings_toggle_1v1')" />

      <div v-show="bottomBarStatus === 0" class="box">
        <!--청취 태블렛에서 버튼 보임
          <ButtonGuage label-text="마이크 감도" />
          <ButtonGuage label-text="음성 구분 조절" />
        -->
        <!--마이크장치목록/마이크버튼-->
        <template v-if="isVisibleMic">
          <ButtonToggleMicSetting :mode="typeCall" :using-tss="['GENERAL', 'CONFERENCE'].includes(typeCall)" />
          <ButtonToggleMic check-audio :signal-mode="['MEETING'].includes(typeCall)" :error-message="micErrorMessage" />
        </template>

        <ButtonToggleSystemAudioMic
          v-if="isSystemAudioMicrophoneEnabled"
          :signal-mode="['MEETING'].includes(typeCall)"
        />

        <!--자동재생버튼-->
        <ButtonToggleSpeaker v-if="isVisibleSpeaker" :type-call="typeCall" />

        <!--언어선택버튼-->
        <ButtonToggleLanguage v-if="isVisibleLanguage" :type-call="typeCall" />

        <!--셋팅버튼-->
        <ButtonToggleSettings v-if="isVisibleSetting" :type-call="typeCall" :domain="domain" />

        <!--MCSP전용버튼-->
        <template v-if="isMCSPEnabled">
          <ButtonContext />
          <ButtonMicSetting />
          <ButtonUserSetting />
          <ButtonMicMute />
        </template>

        <!--MCS전용버튼-->
        <template v-if="isMCSEnabled">
          <ButtonContext />
        </template>

        <!--DM버튼-->
        <template v-if="isVisibleDM">
          <ButtonDM />
        </template>

        <!--종료버튼-->
        <ButtonHangup v-if="isVisibleExit" :handle-hang-up @exit="exitEvent" />
      </div>
      <BottomBar :bottom-bar-status="bottomBarStatus" :handle-reconnect="connectToWebsocket" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { inject, onUnmounted, computed } from 'vue';
import { provide, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import BottomBar from '@/components/domains/chat/common/BottomBar.vue';
import ButtonContext from '@/components/domains/chat/common/ButtonContext.vue';
import ButtonDM from '@/components/domains/chat/common/ButtonDM.vue';
import ButtonHangup from '@/components/domains/chat/common/ButtonHangup.vue';
import ButtonMicMute from '@/components/domains/chat/common/ButtonMicMute.vue';
import ButtonMicSetting from '@/components/domains/chat/common/ButtonMicSetting.vue';
import ButtonToggleLanguage from '@/components/domains/chat/common/ButtonToggleLanguage.vue';
import ButtonToggleMic from '@/components/domains/chat/common/ButtonToggleMic.vue';
import ButtonToggleMicSetting from '@/components/domains/chat/common/ButtonToggleMicSetting.vue';
import ButtonToggleSettings from '@/components/domains/chat/common/ButtonToggleSettings.vue';
import ButtonToggleSpeaker from '@/components/domains/chat/common/ButtonToggleSpeaker.vue';
import ButtonToggleSystemAudioMic from '@/components/domains/chat/common/ButtonToggleSystemAudioMic.vue';
import ButtonUserSetting from '@/components/domains/chat/common/ButtonUserSetting.vue';
import LogoMainNav from '@/components/domains/chat/common/LogoMainNav.vue';
import { useButtonToggleMic } from '@/composables/domains/chat/useButtonToggleMic';
import { useSocketCall } from '@/composables/domains/chat/useSocketCall';
import { useNotification } from '@/composables/shared/useNotification';
import { useAudioStore } from '@/stores/audio';
import { useLanguageStore } from '@/stores/language';
import { useSocketStore } from '@/stores/socket';
import { LanguageCode, TypeCall, TypeDomain } from '@/types/common';
import { ChatFooterOptions } from '@/types/components';
import { DEFAULT_CHAT_UI_STATE } from '@/utils/constants/chat';
import {
  ij_bottomBarStatus,
  ij_chat_mode,
  ij_currentPopupMicInput,
  ij_setBottomBarStatus,
  ij_setCurrentPopupMicInput,
  ij_isSystemAudioMicrophoneEnabled,
  ij_chat_ui,
} from '@/utils/injectionKeys.js';

const props = withDefaults(
  defineProps<{
    typeCall: TypeCall;
    domain: TypeDomain;
    listButtonVisible: ChatFooterOptions[];
  }>(),
  {
    typeCall: 'GENERAL',
    domain: 'CHAT',
    listButtonVisible: () => [],
  }
);

const { t } = useI18n();

const socketStore = useSocketStore();
const audioStore = useAudioStore();
const languageStore = useLanguageStore();

const { isVisibleMic: isMicEnabled } = useButtonToggleMic();
const { alertGoToCallEntrance } = useNotification();

const bottomBarStatus = inject(ij_bottomBarStatus, ref(0));
const setBottomBarStatus = inject(ij_setBottomBarStatus) as (statusNo: number) => void;
const chatMode = inject<'DEFAULT' | 'SELF_SERVICE'>(ij_chat_mode, 'DEFAULT');
const { layout } = inject(ij_chat_ui, DEFAULT_CHAT_UI_STATE);

// 통역 종료 상태를 관리하는 ref 변수 추가
const currentPopupMicInput = ref('');
let checkAttendeesIntervalId;

const { connectToWebsocket } = useSocketCall(setBottomBarStatus);

// 마이크 노출 여부
const isVisibleMic = computed<boolean>(() => {
  return props.listButtonVisible.includes('MICROPHONE') && !!isMicEnabled.value;
});

// 시스템 오디오 마이크 노출 여부
const isSystemAudioMicrophoneEnabled = computed<boolean>(() => {
  const isNonTouchDevice = navigator.maxTouchPoints === 0;
  return (
    props.listButtonVisible.includes('SYSTEM_AUDIO_MICROPHONE') &&
    !!isMicEnabled.value &&
    (layout.isDesktopSize.value || isNonTouchDevice)
  );
});

// 스피커 노출 여부
const isVisibleSpeaker = computed<boolean>(() => {
  return props.listButtonVisible.includes('SPEAKER');
});

// 언어선택 노출 여부
const isVisibleLanguage = computed<boolean>(() => {
  return props.listButtonVisible.includes('LANGUAGE');
});

// MCSP 전용 버튼 노출 여부
const isMCSPEnabled = computed<boolean>(() => {
  return props.listButtonVisible.includes('MCSP');
});

// MCS 전용 버튼 노출 여부
const isMCSEnabled = computed<boolean>(() => {
  return props.listButtonVisible.includes('MCS');
});

// 셋팅 노출 여부
const isVisibleSetting = computed<boolean>(() => {
  return props.listButtonVisible.includes('SETTING') && (!layout.isMobileSize.value || props.typeCall === 'SINGLE');
});

// 셋팅 노출 여부
const isVisibleDM = computed<boolean>(() => {
  return props.listButtonVisible.includes('DM') && !layout.isMobileSize.value;
});

// 종료 노출 여부
const isVisibleExit = computed<boolean>(() => {
  return props.listButtonVisible.includes('EXIT') && !layout.isMobileSize.value;
});

const micErrorMessage = computed<string>(() => {
  if (audioStore.isMutedByEmployee) {
    return props.typeCall === 'MEETING' ? t('mic_muted_by_other_host') : t('mic_muted_by_host');
  }

  const code: LanguageCode = audioStore.languageSelected?.code;
  if (
    ['GENERAL', 'CONFERENCE'].includes(props.typeCall) &&
    !languageStore.interpretationLanguageOptions.inputEnabled.find((option) => option.code === code)
  ) {
    return t('error_language_input_not_supported');
  }

  return '';
});

// 팝업 설정
const setCurrentPopupMicInput = (popupName) => {
  currentPopupMicInput.value = popupName !== currentPopupMicInput.value ? popupName : '';
};

const exitEvent = () => {
  return ['GENERAL', 'MEETING', 'CONFERENCE'].includes(props.typeCall) && chatMode === 'DEFAULT'
    ? alertGoToCallEntrance
    : () => {};
};

provide(ij_setCurrentPopupMicInput, setCurrentPopupMicInput);
provide(ij_currentPopupMicInput, currentPopupMicInput);
provide(ij_isSystemAudioMicrophoneEnabled, isSystemAudioMicrophoneEnabled);

onUnmounted(() => {
  if (checkAttendeesIntervalId) clearInterval(checkAttendeesIntervalId);
  if (socketStore.socket) {
    setBottomBarStatus(2);
    socketStore.sendMessageDisconnect();
    socketStore.close();
  }
  socketStore.channel?.close();
});
</script>

<style scoped lang="scss">
.mic-input-container {
  position: relative;
  bottom: env(safe-area-inset-bottom, 0px);
  left: 0;
  margin-top: 0;
  width: 100%;
  min-height: 5.125rem;
  display: flex;
  background: var(--background-callinputmic);
  border-top: solid 1px var(--border-dim);
  justify-content: center;
  z-index: $z-footer;
  align-items: center;
}

.bg-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  max-width: 75rem;
  backdrop-filter: $blur-light;
}

.box {
  position: relative;
  display: flex;
  width: fit-content;
  max-width: 25rem;
  padding: 0.25rem 0 0.75rem 0;
  gap: 0.25rem;
  justify-content: center;
  align-items: center;
}
</style>
